import React, { useEffect, useMemo, useRef, useState } from "react";
import linking from "./LinkingConfiguration";
// react-navigation
import { DefaultTheme, NavigationContainer } from "@react-navigation/native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { navigationRef } from "../RootNavigation";

// types
import { RootStackParamList } from "./types";

// screens
import GroupReservation from "screens/GroupReservation/GroupReservation";
import Legal from "screens/Legal/Legal";

// store
import { setRedirect } from "features/slices/loginFlowSlice";
import { setTheme } from "features/slices/themeSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";

import { isDev } from "config/env";
import useIsSSO from "hooks/useIsSSO";
import toast from "react-native-toast-message";
import BallParkAccountNotFound from "screens/BallparkAccountNotFound/BallparkAccountNotFound";
import BuyGift from "screens/BuyGift/BuyGift";
import ClaimGift from "screens/ClaimGift/ClaimGift";
import RenewalOptIn from "screens/RenewalOptIn/RenewalOptIn";
import ClaimGuestPasses from "screens/ClaimGuestPasses/ClaimGuestPasses";
import FinishSignup from "screens/FinishSignup/FinishSignup";
import RsvpsGeneral from "screens/Rsvps/RsvpsGeneral";
import RsvpsPersonal from "screens/Rsvps/RsvpsPersonal";
import Shop from "screens/Shop/Shop";
import ShopPhaseTwo from "screens/ShopPhaseTwo/ShopPhaseTwo";
import ShopV3 from "screens/ShopV3/ShopV3";
import SSOPlayerNotFound from "screens/SSOPlayerNotFound/SSOPlayerNotFound";
import { trackPageView } from "utils/analytics";
import { generateThemeColor } from "utils/appUtils";
import AuthenticatedFlow from "./AuthenticatedFlow";
import LoginFlowStack from "./LoginFlow";

export default function Navigation() {
  const routeNameRef = useRef<string>();
  const [navReady, setNavReady] = useState(false);
  const MyTheme = {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      background: "white",
    },
  };
  return (
    <NavigationContainer
      linking={linking}
      ref={navigationRef}
      documentTitle={{
        formatter: (options, route) =>
          `${options?.title ?? route?.name} - FanRally`,
      }}
      onReady={() => {
        routeNameRef.current = navigationRef.getCurrentRoute()!.name;
        setNavReady(true);
      }}
      onStateChange={async () => {
        if (isDev) return;
        const previousRouteName = routeNameRef.current;
        const currentRouteName = navigationRef.getCurrentRoute()!.name;

        if (previousRouteName !== currentRouteName) {
          trackPageView();
        }
        // Save the current route name for later comparison
        routeNameRef.current = currentRouteName;
      }}
      theme={MyTheme}
    >
      <RootNavigator navReady={navReady}></RootNavigator>
    </NavigationContainer>
  );
}

const Stack = createNativeStackNavigator<RootStackParamList>();

function RootNavigator({ navReady }: { navReady: boolean }) {
  const storedUserId = useAppSelector((state) => state.auth.id);
  const theme = useAppSelector((state) => state.theme.theme);
  const { tenantThemeColor, tenantSubdomain } = useAppSelector(
    (state) => state.tenant,
  );
  const redirectState = useAppSelector((state) => state.loginFlow.redirect);
  const { authMethod, isSSO } = useIsSSO();
  const dispatch = useAppDispatch();

  const customTheme = generateThemeColor(tenantThemeColor);
  const initCustomTheme = () => {
    dispatch(setTheme(customTheme));
  };

  const isUserLoggedIn = useMemo(() => {
    // If app is running inside the Ballpark app
    if (isSSO) {
      return true;
    }
    return storedUserId !== undefined;
  }, [storedUserId, isSSO, tenantSubdomain]);

  useEffect(() => {
    initCustomTheme(); // tenant theme
  }, []);

  useEffect(() => {
    if (!navReady || !isUserLoggedIn || redirectState === undefined) return;
    if (!navigationRef.isReady()) return;
    navigationRef.resetRoot(redirectState);
    dispatch(setRedirect(undefined));
  }, [navReady, isUserLoggedIn, redirectState, dispatch]);

  return (
    <Stack.Navigator
      screenOptions={{ headerShown: false }}
      screenListeners={{
        state() {
          toast.hide();
        },
      }}
    >
      {isUserLoggedIn ? (
        <Stack.Screen name="AuthenticatedFlow" component={AuthenticatedFlow} />
      ) : (
        <Stack.Screen
          name="LoginFlow"
          component={LoginFlowStack}
          options={{ title: "Login" }}
        />
      )}
      <Stack.Screen
        name="GroupReservation"
        component={GroupReservation}
        options={{ title: "Group reserve" }}
      />
      <Stack.Screen
        name="ClaimSeats"
        component={ClaimGuestPasses}
        options={{ title: "Claim seats" }}
      />
      <Stack.Screen
        name="ClaimGuestPasses"
        component={ClaimGuestPasses}
        options={{ title: "Claim guest passes" }}
      />
      <Stack.Screen name="Legal" component={Legal} />
      <Stack.Screen
        name="Shop"
        component={Shop}
        options={{ title: "Shop plans" }}
      />
      <Stack.Screen
        name="ShopPagePhaseTwo"
        component={ShopPhaseTwo}
        options={{ title: "Shop plans" }}
      />
      <Stack.Screen
        name="ShopV3"
        component={ShopV3}
        options={{ title: "Shop plans" }}
      />
      <Stack.Screen
        name="FinishSignup"
        component={FinishSignup}
        options={{ title: "Finish signup" }}
      />
      <Stack.Screen
        name="BallparkAccountNotFound"
        component={BallParkAccountNotFound}
        options={{ title: "Ballpark Account Not Found" }}
      />
      <Stack.Screen
        name="BuyGift"
        component={BuyGift}
        options={{ title: "Buy Gift" }}
      />
      <Stack.Screen
        name="ClaimGift"
        component={ClaimGift}
        options={{ title: "Claim Gift" }}
      />
      <Stack.Screen
        name="RenewalOptIn"
        component={RenewalOptIn}
        options={{ title: "Renewal Opt-In" }}
      />
      <Stack.Screen
        name="RsvpsGeneral"
        component={RsvpsGeneral}
        options={{ title: "RSVP" }}
      />
      <Stack.Screen
        name="RsvpsPersonal"
        component={RsvpsPersonal}
        options={{ title: "RSVP" }}
      />
      <Stack.Screen
        name="SSOPlayerNotFound"
        component={SSOPlayerNotFound}
        options={{ title: "Account Not Found" }}
      />
    </Stack.Navigator>
  );
}
